import type { MarketingLocale } from '@shared/locale';

/**
 * Copy for the solution pages.
 *
 * Held here rather than in the `translations` table because these pages are new
 * — there is nothing in the panel to preserve, and the seed file is not a place
 * to add 150 keys by hand. Each entry carries both locales, so `/solutions/...`
 * and `/en/solutions/...` render from the same source. If the team later wants
 * to edit this copy from the admin panel, the shape maps one-to-one onto
 * translation keys.
 *
 * GROUND RULES for anything added here, learned from what this site already had
 * to remove from /blog and /changelog:
 *
 *   - Describe only capabilities that exist in the product today. Every claim
 *     below maps to a shipped dashboard route or service.
 *   - No performance statistics. No "%98 accuracy", no "40% fewer no-shows".
 *     If a number is not measured and attributable, it does not go on the page.
 *   - Marketplace connectors (Uber Eats, DoorDash, Yemeksepeti order sync) are
 *     NOT integrations today — they are catalogue entries with `connected:
 *     false` and no backend. Do not describe them as working.
 */

export interface SolutionSection {
  heading: string;
  body: string;
  /** Concrete, checkable specifics — not benefits. */
  points?: string[];
}

export interface Solution {
  slug: string;
  /** Emoji used as a lightweight card icon, matching the FAQ page's style. */
  icon: string;
  title: string;
  /** One sentence under the H1. */
  lede: string;
  metaTitle: string;
  metaDescription: string;
  sections: SolutionSection[];
  /** What the reader should check before expecting this to work. */
  requirements: string[];
}

type Localised = Record<MarketingLocale, Solution[]>;

const tr: Solution[] = [
  {
    slug: 'kacan-cagrilar',
    icon: '📞',
    title: 'Kaçan aramaları siparişe çevirin',
    lede:
      'Yoğun saatte çalan telefona kimse bakamaz. Asistan her aramayı karşılar, siparişi alır ve mutfağa iletir.',
    metaTitle: 'Kaçan Çağrı Çözümü — Restoran Telefon Asistanı | Alo Restoran',
    metaDescription:
      'Yoğun saatte cevaplanamayan aramalar siparişe dönüşmeden kayboluyor. Alo Restoran asistanı her çağrıyı karşılar, siparişi alır ve doğrudan mutfağa iletir.',
    sections: [
      {
        heading: 'Sorun yoğun saatte ortaya çıkıyor',
        body:
          'Akşam servisinde salon doluyken telefonun çalması, birinin mutfaktan ya da masadan ayrılması demek. Çoğu restoran bu saatlerde aramaları ya geç yanıtlıyor ya hiç yanıtlayamıyor — ve arayan müşteri ikinci kez aramıyor, sıradaki restorana geçiyor.',
        points: [
          'Kaçan her arama, karşılığı ölçülmeyen bir sipariş kaybı',
          'Telefona bakan personel o sırada salondaki müşteriye bakmıyor',
          'Yoğunluk arttıkça kayıp oranı da artıyor — yani en kârlı saatte en çok kaybediyorsunuz',
        ],
      },
      {
        heading: 'Asistan ne yapıyor',
        body:
          'Yapay zeka asistanı telefon hattınıza bağlanır ve aramaları kendi menünüz ile restoran bilgilerinizi kullanarak karşılar. Siparişi alır, adresi ve notları toplar, sipariş panele ve mutfak ekranına düşer. Halledemediği bir durumda, tanımladığınız kurallara göre personeli bilgilendirebilir veya görüşmeyi aktarabilir.',
        points: [
          'Telefon altyapısı Twilio hesabınız ya da SIP/Jambonz hattınız üzerinden çalışır',
          'Menü, çalışma saatleri ve bilgi tabanı panelden yönetilir',
          'Görüşme dökümleri ve çağrı kayıtları panelde saklanır',
        ],
      },
      {
        heading: 'Ölçebileceğiniz şey',
        body:
          'Ana sayfadaki ROI hesaplayıcı, kendi çağrı hacminizle olası sipariş değerini tahmin etmenizi sağlar. Bu bir planlama tahminidir, garanti edilen gelir değildir — gerçek sonucu kendi panelinizdeki çağrı ve sipariş verisiyle ölçersiniz.',
      },
    ],
    requirements: [
      'Twilio hesabı veya yapılandırılmış bir SIP hattı',
      'Panele girilmiş güncel menü ve çalışma saatleri',
      'Devretme kuralları için bir personel telefonu veya bildirim kanalı',
    ],
  },
  {
    slug: 'whatsapp-siparis',
    icon: '💬',
    title: "WhatsApp'tan gelen siparişleri tek yerde toplayın",
    lede:
      'Müşteriler zaten WhatsApp yazıyor. Asistan yanıtlar, sipariş ve rezervasyon alır, konuşma panele düşer.',
    metaTitle: 'WhatsApp Sipariş Yönetimi — Restoran Asistanı | Alo Restoran',
    metaDescription:
      "WhatsApp Cloud API ile gelen mesajları yapay zeka asistanı yanıtlar, sipariş ve rezervasyon alır. Tüm görüşmeler tek operasyon panelinde toplanır.",
    sections: [
      {
        heading: 'WhatsApp bir sipariş kanalı, ama yönetilmiyor',
        body:
          'Türkiye’de müşteri aramak yerine yazıyor. Sorun mesajın gelmesi değil, kimin ne zaman yanıtladığının belirsiz olması: mesajlar bir çalışanın kişisel telefonunda kalıyor, sipariş sözlü aktarılıyor, kayıt tutulmuyor.',
      },
      {
        heading: 'Nasıl çalışıyor',
        body:
          'Onaylı bir Meta WhatsApp Business hesabını kendi kimlik bilgileriniz ve şablonlarınızla bağlarsınız. Asistan gelen mesajı menünüz ve bilgi tabanınızla yanıtlar; sipariş veya rezervasyon oluşturur. Görüşme, web sohbeti ve telefon görüşmeleriyle aynı panelde, konuşma bağlamıyla birlikte görünür.',
        points: [
          'WhatsApp Cloud API — kendi Meta Business hesabınız',
          'Şablon mesajları ve dil ayarları panelden yönetilir',
          'Görüşmeler, siparişler ve müşteri geçmişi tek kayıtta birleşir',
        ],
      },
    ],
    requirements: [
      'Onaylanmış Meta WhatsApp Business hesabı ve şablonları',
      'Meta tarafında doğrulanmış işletme bilgileri',
      'Panelde tanımlı menü ve yanıt kuralları',
    ],
  },
  {
    slug: 'rezervasyon-yonetimi',
    icon: '📅',
    title: 'Rezervasyonları tek panelden yönetin',
    lede:
      'Masa müsaitliği, onaylar ve hatırlatmalar defterde değil, tek ekranda.',
    metaTitle: 'Restoran Rezervasyon Yönetimi Yazılımı | Alo Restoran',
    metaDescription:
      'Masa müsaitliğini, rezervasyonları, onayları ve hatırlatma akışlarını tek panelden yönetin. Rezervasyonlar telefon, WhatsApp ve web sohbetinden gelir.',
    sections: [
      {
        heading: 'Defter ve telefon arasında kaybolan rezervasyonlar',
        body:
          'Rezervasyon bir deftere, bir de birinin aklına yazılıyor. Kim onayladı, müşteri hatırlatıldı mı, masa gerçekten boş mu — bunların hiçbiri sonradan kontrol edilemiyor.',
      },
      {
        heading: 'Panelde ne var',
        body:
          'Masa müsaitliği, gelen rezervasyonlar, onay durumları ve yapılandırılmış hatırlatma akışları tek ekranda toplanır. Rezervasyon hangi kanaldan geldiyse — telefon, WhatsApp, web sohbeti — aynı listeye düşer.',
        points: [
          'Masaya atanmamış rezervasyonlar ayrıca işaretlenir',
          'Onay ve hatırlatma akışları yapılandırılabilir',
          'Rezervasyon geçmişi müşteri kaydına bağlanır',
        ],
      },
    ],
    requirements: [
      'Panelde tanımlı masa düzeni ve kapasite',
      'Hatırlatma göndermek için etkin bir mesajlaşma kanalı',
    ],
  },
  {
    slug: 'qr-menu-siparis',
    icon: '📱',
    title: 'Masadan QR ile sipariş',
    lede:
      'Müşteri masadaki kodu okutur, menüyü görür ve siparişi doğrudan verir.',
    metaTitle: 'QR Menü ve Masadan Sipariş Sistemi | Alo Restoran',
    metaDescription:
      'Masadaki QR kodla müşteri menüyü açar ve siparişini kendisi verir. Sipariş doğrudan mutfağa ve sipariş paneline düşer.',
    sections: [
      {
        heading: 'QR menü tek başına yeterli değil',
        body:
          'Çoğu QR menü sadece bir PDF açıyor. Müşteri menüyü görüyor ama sipariş vermek için yine garson bekliyor — yani sıra beklemek ortadan kalkmıyor, sadece kâğıt ortadan kalkıyor.',
      },
      {
        heading: 'Sipariş alan QR menü',
        body:
          'Her şube ve masa için ayrı bir bağlantı oluşturulur. Müşteri kodu okuttuğunda menüyü görür, seçimini yapar ve siparişi gönderir. Sipariş, telefon ve WhatsApp siparişleriyle aynı akışa girer; mutfak ekranında görünür.',
        points: [
          'Şube ve masa bazında ayrı bağlantı',
          'Menü değişikliği panelden yapılır, kod değişmez',
          'Siparişler diğer kanallarla aynı panelde birleşir',
        ],
      },
    ],
    requirements: [
      'Panelde tanımlı güncel menü ve fiyatlar',
      'Masalara yerleştirilecek basılı QR kodlar',
    ],
  },
  {
    slug: 'cok-subeli-yonetim',
    icon: '🏢',
    title: 'Birden fazla şubeyi tek panelden yönetin',
    lede:
      'Menü, personel ve sipariş akışı her şube için ayrı ayrı değil, tek yerden.',
    metaTitle: 'Çok Şubeli Restoran Yönetimi | Alo Restoran',
    metaDescription:
      'Tüm lokasyonlarınız için tek pano: şube bazlı menü, personel yetkileri, sipariş akışı ve analitik tek yerden yönetilir.',
    sections: [
      {
        heading: 'Şube sayısı arttıkça görünürlük azalıyor',
        body:
          'İki şubede telefonla halledilen şey, beş şubede halledilemiyor. Hangi şube hangi siparişi aldı, hangi menü güncel, kimin neye yetkisi var — bunlar ayrı ayrı takip edilemez hale geliyor.',
      },
      {
        heading: 'Tek pano',
        body:
          'Şubeler, menüler, personel ve yetkiler tek panelde yönetilir. Siparişler şube bazında ayrışır, analitik hem şube hem toplam düzeyde görüntülenir.',
        points: [
          'Şube bazlı menü ve fiyat farkları',
          'Personel yetkileri rol bazında tanımlanır',
          'Sipariş ve rezervasyonlar şubeye göre filtrelenir',
        ],
      },
    ],
    requirements: [
      'Her şube için tanımlı adres, çalışma saati ve menü',
      'Şube bazında telefon veya mesajlaşma kanalı',
    ],
  },
];

const en: Solution[] = [
  {
    slug: 'kacan-cagrilar',
    icon: '📞',
    title: 'Turn missed calls into orders',
    lede:
      'Nobody can answer the phone during a rush. The assistant takes every call, captures the order and sends it to the kitchen.',
    metaTitle: 'Missed Call Recovery for Restaurants | Alo Restoran',
    metaDescription:
      'Calls that go unanswered during a rush never become orders. The Alo Restoran assistant answers every call, takes the order and sends it straight to the kitchen.',
    sections: [
      {
        heading: 'The problem shows up exactly when you are busiest',
        body:
          'When the dining room is full, answering the phone means pulling someone off the floor or out of the kitchen. Most restaurants answer late or not at all during those hours — and the caller does not try again, they call the next restaurant.',
        points: [
          'Every missed call is a lost order nobody measures',
          'Whoever answers the phone is not serving the guest in front of them',
          'The busier you are, the more you lose — so the loss peaks in your most profitable hours',
        ],
      },
      {
        heading: 'What the assistant does',
        body:
          'The AI assistant connects to your phone line and answers using your own menu and restaurant details. It takes the order, collects the address and notes, and the order lands in the dashboard and on the kitchen screen. When it cannot handle something, it can notify staff or transfer the call according to the rules you set.',
        points: [
          'Telephony runs on your own Twilio account or your SIP/Jambonz line',
          'Menu, opening hours and knowledge base are managed from the dashboard',
          'Transcripts and call records are kept in the dashboard',
        ],
      },
      {
        heading: 'What you can measure',
        body:
          'The ROI calculator on the home page estimates the order value at stake for your own call volume. It is a planning estimate, not guaranteed revenue — you measure the real result from the call and order data in your own dashboard.',
      },
    ],
    requirements: [
      'A Twilio account or a configured SIP line',
      'An up-to-date menu and opening hours in the dashboard',
      'A staff phone or notification channel for escalation rules',
    ],
  },
  {
    slug: 'whatsapp-siparis',
    icon: '💬',
    title: 'Bring WhatsApp orders into one place',
    lede:
      'Customers already message you. The assistant replies, takes orders and reservations, and the conversation lands in the dashboard.',
    metaTitle: 'WhatsApp Order Management for Restaurants | Alo Restoran',
    metaDescription:
      'An AI assistant answers WhatsApp Cloud API messages, takes orders and reservations, and collects every conversation in one operations dashboard.',
    sections: [
      {
        heading: 'WhatsApp is an order channel, but an unmanaged one',
        body:
          'Customers write instead of calling. The problem is not the message arriving — it is that nobody knows who answered it or when. Messages sit on a staff member’s personal phone, the order is relayed verbally, and nothing is recorded.',
      },
      {
        heading: 'How it works',
        body:
          'You connect an approved Meta WhatsApp Business account with your own credentials and templates. The assistant answers using your menu and knowledge base and creates an order or reservation. The conversation appears in the same dashboard as web chat and phone calls, with its context attached.',
        points: [
          'WhatsApp Cloud API — your own Meta Business account',
          'Template messages and language settings are managed from the dashboard',
          'Conversations, orders and customer history are joined in one record',
        ],
      },
    ],
    requirements: [
      'An approved Meta WhatsApp Business account and templates',
      'Business details verified on Meta’s side',
      'A menu and reply rules configured in the dashboard',
    ],
  },
  {
    slug: 'rezervasyon-yonetimi',
    icon: '📅',
    title: 'Run reservations from one dashboard',
    lede: 'Table availability, confirmations and reminders on one screen instead of in a notebook.',
    metaTitle: 'Restaurant Reservation Management Software | Alo Restoran',
    metaDescription:
      'Manage table availability, reservations, confirmations and reminder flows from one dashboard. Reservations arrive from phone, WhatsApp and web chat.',
    sections: [
      {
        heading: 'Reservations lost between a notebook and a phone call',
        body:
          'A reservation gets written in a book and in somebody’s head. Who confirmed it, whether the guest was reminded, whether the table is actually free — none of it can be checked afterwards.',
      },
      {
        heading: 'What the dashboard holds',
        body:
          'Table availability, incoming reservations, confirmation status and configurable reminder flows sit on one screen. Whichever channel a reservation came from — phone, WhatsApp, web chat — it lands in the same list.',
        points: [
          'Reservations without an assigned table are flagged separately',
          'Confirmation and reminder flows are configurable',
          'Reservation history is attached to the customer record',
        ],
      },
    ],
    requirements: [
      'Table layout and capacity configured in the dashboard',
      'An active messaging channel for sending reminders',
    ],
  },
  {
    slug: 'qr-menu-siparis',
    icon: '📱',
    title: 'Order from the table with a QR code',
    lede: 'The guest scans the code at the table, sees the menu and orders directly.',
    metaTitle: 'QR Menu and Table Ordering System | Alo Restoran',
    metaDescription:
      'Guests scan the QR code at the table, open the menu and place the order themselves. The order goes straight to the kitchen and the order dashboard.',
    sections: [
      {
        heading: 'A QR menu on its own does not help much',
        body:
          'Most QR menus open a PDF. The guest can read the menu but still waits for a server to order — so the waiting did not go away, only the paper did.',
      },
      {
        heading: 'A QR menu that takes the order',
        body:
          'A separate link is generated per branch and table. When the guest scans it they see the menu, make their choices and send the order. It joins the same flow as phone and WhatsApp orders and appears on the kitchen screen.',
        points: [
          'A distinct link per branch and table',
          'Menu changes are made in the dashboard; the printed code stays the same',
          'Orders merge with the other channels in one dashboard',
        ],
      },
    ],
    requirements: [
      'An up-to-date menu with prices in the dashboard',
      'Printed QR codes placed on the tables',
    ],
  },
  {
    slug: 'cok-subeli-yonetim',
    icon: '🏢',
    title: 'Run several branches from one dashboard',
    lede: 'Menu, staff and order flow in one place rather than per location.',
    metaTitle: 'Multi-Branch Restaurant Management | Alo Restoran',
    metaDescription:
      'One dashboard for every location: per-branch menus, staff permissions, order flow and analytics managed from a single place.',
    sections: [
      {
        heading: 'Visibility drops as branches are added',
        body:
          'What two branches handle over the phone, five branches cannot. Which branch took which order, which menu is current, who is allowed to do what — none of it stays trackable separately.',
      },
      {
        heading: 'One dashboard',
        body:
          'Branches, menus, staff and permissions are managed in one place. Orders are separated by branch, and analytics can be read per branch or in total.',
        points: [
          'Per-branch menus and price differences',
          'Staff permissions defined by role',
          'Orders and reservations filtered by branch',
        ],
      },
    ],
    requirements: [
      'Address, opening hours and menu configured for each branch',
      'A phone or messaging channel per branch',
    ],
  },
];

const SOLUTIONS: Localised = { tr, en };

export const SOLUTION_SLUGS = tr.map((s) => s.slug);

export function getSolutions(locale: MarketingLocale): Solution[] {
  return SOLUTIONS[locale] ?? SOLUTIONS.tr;
}

export function getSolution(locale: MarketingLocale, slug: string): Solution | undefined {
  return getSolutions(locale).find((s) => s.slug === slug);
}

export const HUB_COPY: Record<MarketingLocale, {
  badge: string;
  title: string;
  lede: string;
  metaTitle: string;
  metaDescription: string;
  requirementsHeading: string;
  ctaTitle: string;
  ctaBody: string;
  ctaLink: string;
  backToHub: string;
  readMore: string;
}> = {
  tr: {
    badge: 'Çözümler',
    title: 'Restoranın hangi işini çözmek istiyorsunuz?',
    lede:
      'Her çözüm, panelde bugün var olan bir özelliğe karşılık gelir. Hangi kanalları açacağınıza kendiniz karar verirsiniz.',
    metaTitle: 'Çözümler — Restoran Sipariş ve Rezervasyon Otomasyonu | Alo Restoran',
    metaDescription:
      'Kaçan çağrılar, WhatsApp siparişi, rezervasyon yönetimi, QR menü ve çok şubeli yönetim. Alo Restoran’ın restoran operasyonunda çözdüğü işler.',
    requirementsHeading: 'Çalışması için gerekenler',
    ctaTitle: 'Kendi menünüzle görelim',
    ctaBody:
      '30 dakikalık bir görüşmede kendi menünüz ve çağrı akışınızla nasıl çalıştığını gösterelim. Taahhüt gerekmez.',
    ctaLink: 'İletişime geçin',
    backToHub: 'Tüm çözümler',
    readMore: 'Devamını okuyun',
  },
  en: {
    badge: 'Solutions',
    title: 'Which part of the restaurant are you trying to fix?',
    lede:
      'Each solution maps to a feature that exists in the dashboard today. You decide which channels to turn on.',
    metaTitle: 'Solutions — Restaurant Order and Reservation Automation | Alo Restoran',
    metaDescription:
      'Missed calls, WhatsApp ordering, reservation management, QR menu ordering and multi-branch operations — what Alo Restoran solves in restaurant operations.',
    requirementsHeading: 'What it needs to work',
    ctaTitle: 'See it with your own menu',
    ctaBody:
      'In a 30-minute call we will show how it works with your menu and your call flow. No commitment.',
    ctaLink: 'Get in touch',
    backToHub: 'All solutions',
    readMore: 'Read more',
  },
};
